import { useCallback, useMemo } from "react";
import {
  AlertTriangle,
  Check,
  Lightbulb,
  LineChart,
  Loader2,
  PenLine,
  Sparkles,
  User,
  X,
} from "lucide-react";
import { splitIntoInsightBuckets } from "@/components/AiInsightsCard";
import InterventionActionPlan from "@/components/InterventionActionPlan";
import HeroStatusChip from "@/components/HeroStatusChip";
import { cardsToInsightLines, registerToInsightLines } from "@/utils/insightActionPlanDefaults";
import { useMistralFirstInsights } from "@/hooks/useMistralFirstInsights";
import { parseInsightBullets } from "@/utils/parseInsightBullets";
import {
  bulletsToActionQueue,
  bulletsToApaarTrackerRows,
  bulletsToDropboxNarratives,
  bulletsToDropboxSummaryCards,
} from "@/utils/mistralInsightMappers";
import {
  buildDropboxInsightsMarkdown,
  buildExecutiveSummaryCards,
  buildInsightActionQueue,
  buildInsightsHeroStats,
  buildInsightsPlaybook,
  buildRootCauseNarratives,
  buildSignalStrengthMatrix,
} from "@/utils/dropboxDerived";
import { formatDataFreshnessLabel } from "@/utils/dataFreshness";
import "./DropboxInsightsTab.css";
import { toTitleCase } from "../utils/toTitleCase";

const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const DECISION_ICONS = {
  green: Check,
  red: AlertTriangle,
  orange: User,
};

const NARRATIVE_ICONS = {
  green: Check,
  red: X,
  orange: LineChart,
  purple: Lightbulb,
};

const PLAYBOOK_ICONS = {
  red: AlertTriangle,
  orange: User,
  purple: Lightbulb,
};

const DropboxInsightsTab = ({ overview = {}, dataQuality = {}, blockData = [] }) => {
  const hasData = Boolean(overview?.schools_with_remarks || overview?.total_remarks);

  const buildLocalMarkdown = useCallback(
    () => buildDropboxInsightsMarkdown(overview, dataQuality, blockData),
    [overview, dataQuality, blockData]
  );

  const scopeKey = useMemo(
    () =>
      JSON.stringify({
        r: overview?.total_remarks,
        e: dataQuality?.error_count,
        p: dataQuality?.pending_count,
      }),
    [overview?.total_remarks, dataQuality?.error_count, dataQuality?.pending_count]
  );

  const {
    markdown: aiMarkdown,
    loading: insightsLoading,
    generatedAt,
    isMistral,
  } = useMistralFirstInsights({
    kind: "dropbox",
    title: "Dropbox Remarks Insights",
    buildLocalMarkdown,
    context: { overview, dataQuality, blockData },
    enabled: hasData,
    scopeKey,
  });

  const buckets = useMemo(() => splitIntoInsightBuckets(aiMarkdown), [aiMarkdown]);

  const localHeroStats = useMemo(
    () => buildInsightsHeroStats(overview, dataQuality, blockData),
    [overview, dataQuality, blockData]
  );

  const heroStats = useMemo(() => {
    if (insightsLoading) return localHeroStats;
    const insightBullets = parseInsightBullets(buckets.insights);
    const chips = [...localHeroStats.chips];
    if (isMistral) chips.unshift({ text: "Mistral AI", className: "purple" });
    return {
      ...localHeroStats,
      chips,
      primaryInsight: insightBullets[0] || localHeroStats.primaryInsight,
    };
  }, [insightsLoading, buckets.insights, localHeroStats, isMistral]);

  const localSummaryCards = useMemo(
    () => buildExecutiveSummaryCards(overview, dataQuality),
    [overview, dataQuality]
  );
  const summaryCards = useMemo(() => {
    if (insightsLoading) return [];
    const aiCards = bulletsToDropboxSummaryCards(
      parseInsightBullets(buckets.insights),
      localSummaryCards
    );
    return aiCards?.length ? aiCards : localSummaryCards;
  }, [insightsLoading, buckets.insights, localSummaryCards]);

  const localNarratives = useMemo(
    () => buildRootCauseNarratives(overview, dataQuality, blockData),
    [overview, dataQuality, blockData]
  );
  const narratives = useMemo(() => {
    if (insightsLoading) return [];
    const aiNarratives = bulletsToDropboxNarratives(
      parseInsightBullets(buckets.root),
      localNarratives
    );
    return aiNarratives?.length ? aiNarratives : localNarratives;
  }, [insightsLoading, buckets.root, localNarratives]);

  const signalMatrix = useMemo(() => {
    const matrix = buildSignalStrengthMatrix(overview);
    if (insightsLoading) return matrix;
    const insightBullets = parseInsightBullets(buckets.insights);
    const interpretation = insightBullets[insightBullets.length - 1] || matrix.interpretation;
    return { ...matrix, interpretation };
  }, [overview, insightsLoading, buckets.insights]);

  const localActionQueue = useMemo(
    () => buildInsightActionQueue(overview, dataQuality, blockData),
    [overview, dataQuality, blockData]
  );
  const actionQueue = useMemo(() => {
    if (insightsLoading) return [];
    const aiRows = bulletsToApaarTrackerRows(
      parseInsightBullets(buckets.actions),
      localActionQueue.map((r) => ({
        priority: r.priority,
        insight: r.theme,
        evidence: r.evidence,
        owner: r.owner,
        action: r.action,
        status: r.status,
        tagClass: r.tagClass,
      }))
    );
    return aiRows?.length ? aiRows.map((r) => ({
      priority: r.priority,
      theme: r.insight,
      evidence: r.evidence,
      owner: r.owner,
      action: r.action,
      status: r.status,
      tagClass: r.tagClass,
    })) : localActionQueue;
  }, [insightsLoading, buckets.actions, localActionQueue]);

  const localPlaybook = useMemo(
    () => buildInsightsPlaybook(overview, dataQuality),
    [overview, dataQuality]
  );
  const playbook = useMemo(() => {
    if (insightsLoading) return localPlaybook;
    const recBullets = parseInsightBullets(buckets.recs);
    const aiItems = bulletsToActionQueue(recBullets, localPlaybook.items);
    const actionBullets = parseInsightBullets(buckets.actions);
    return {
      items: aiItems?.length ? aiItems : localPlaybook.items,
      footerTitle: localPlaybook.footerTitle,
      footerBody: actionBullets[actionBullets.length - 1] || localPlaybook.footerBody,
    };
  }, [insightsLoading, buckets.recs, buckets.actions, localPlaybook]);

  const defaultRoot = useMemo(() => cardsToInsightLines(localNarratives), [localNarratives]);
  const defaultRecs = useMemo(
    () => cardsToInsightLines(localPlaybook.items),
    [localPlaybook]
  );
  const defaultActions = useMemo(
    () => registerToInsightLines(localActionQueue),
    [localActionQueue]
  );

  const freshnessLabel = useMemo(
    () =>
      insightsLoading
        ? "Generating insights…"
        : generatedAt
          ? formatDataFreshnessLabel(generatedAt)
          : "Ready",
    [insightsLoading, generatedAt]
  );

  return (
    <div className="dbx-insights-tab" data-testid="dropbox-insights">
      <section className="dbx-hero-card" id="dbx-insights-hero">
        <div className="dbx-hero-layout">
          <div>
            <div className="dbx-insights-hero-main">
              <div className="dbx-insights-ai-badge" aria-hidden="true">
                <Sparkles size={52} strokeWidth={1.8} />
              </div>
              <div className="dbx-insights-copy">
                <div className="dbx-page-eyebrow">
                  <PenLine size={14} />
                  {isMistral ? "Mistral AI" : "Rule-based"} · Executive insight layer
                </div>
                <h2>Insights Command Center</h2>
                <p>
                  Transforms raw remarks into a decision-ready narrative: what is working, where risk
                  is rising, why it is happening, and which officer actions should be taken first.
                </p>
                <div className="dbx-status-row">
                  {heroStats.chips.map((chip) => {
                    const actionable = /red|orange|amber/i.test(chip.className ?? "");
                    return (
                      <HeroStatusChip
                        key={chip.text}
                        chipClass="dbx-status-chip"
                        variant={chip.className || undefined}
                        onClick={actionable ? () => scrollToId("dbx-insights-actions") : undefined}
                        ariaLabel={actionable ? `${chip.text} — view insight actions` : undefined}
                      >
                        {chip.text}
                      </HeroStatusChip>
                    );
                  })}
                  {insightsLoading ? (
                    <HeroStatusChip chipClass="dbx-status-chip" showChevron={false}>
                      <Loader2 size={12} className="animate-spin" style={{ display: "inline" }} />{" "}
                      {freshnessLabel}
                    </HeroStatusChip>
                  ) : (
                    <HeroStatusChip chipClass="dbx-status-chip" showChevron={false}>
                      {freshnessLabel}
                    </HeroStatusChip>
                  )}
                </div>
              </div>
            </div>
            <div className="dbx-summary-note">
              <div className="dbx-icon-box purple">
                <Lightbulb size={22} />
              </div>
              <div>
                <b>Primary insight</b>
                <span>{heroStats.primaryInsight}</span>
              </div>
            </div>
          </div>

          <div className="dbx-insights-metric-grid" aria-label="Insight KPIs">
            {heroStats.kpis.map((card) => (
              <article key={card.label} className={`dbx-insights-metric-card ${card.cardClass}`.trim()}>
                <small>{card.label}</small>
                <strong>{card.value}</strong>
                <span>{card.sub}</span>
              </article>
            ))}
          </div>
        </div>
      </section>

      <div className="dbx-section-title" id="dbx-insight-summary">
        <div>
          <h2>AI-Generated Executive Summary</h2>
          <p>
            Instead of one long text block, insights are grouped into decision cards so users can
            quickly separate performance, risk, and intervention priorities.
          </p>
        </div>
        <button type="button" className="dbx-section-action" onClick={() => scrollToId("dbx-insights-actions")}>
          View recommendations →
        </button>
      </div>

      <section className="dbx-insights-decision-grid" aria-label="Executive summary cards">
        {summaryCards.map((card) => {
          const Icon = DECISION_ICONS[card.iconClass] ?? Check;
          return (
            <article key={card.id} className="dbx-insights-decision-card">
              <div className="dbx-insights-decision-head">
                <div className={`dbx-icon-box ${card.iconClass}`.trim()}>
                  <Icon size={22} />
                </div>
                <span className={`dbx-insights-severity ${card.severityClass}`.trim()}>{card.severity}</span>
              </div>
              <h3>{toTitleCase(card.title)}</h3>
              <p>{card.body}</p>
              <ul className="dbx-insights-signal-list">
                {card.signals.map((signal, index) => (
                  <li key={index}>
                    <i />
                    <span>
                      {signal.value ? (
                        <>
                          <b>{signal.value}</b> {signal.label}
                        </>
                      ) : (
                        signal.label
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            </article>
          );
        })}
      </section>

      <div className="dbx-section-title" id="dbx-root-cause">
        <div>
          <h2>Root-Cause Signals</h2>
          <p>
            Root causes are mapped as quantifiable signals, making it easier for district teams to
            validate the AI narrative and assign corrective actions.
          </p>
        </div>
        <button type="button" className="dbx-section-action" onClick={() => scrollToId("dbx-insights-actions")}>
          Create action queue →
        </button>
      </div>

      <section className="dbx-analytics-grid">
        <article className="dbx-panel">
          <div className="dbx-panel-head">
            <div>
              <h3>Evidence-Backed Insight Narrative</h3>
              <p>Operational statements converted into measurable decision lines.</p>
            </div>
            <span className="dbx-pill">AI extracted</span>
          </div>
          <div className="dbx-insights-narrative-list">
            {narratives.map((row) => {
              const Icon = NARRATIVE_ICONS[row.iconClass] ?? Check;
              return (
                <div key={row.id} className="dbx-insights-narrative-item">
                  <div className={`dbx-icon-box ${row.iconClass} dbx-insights-narrative-icon`.trim()}>
                    <Icon size={20} />
                  </div>
                  <div>
                    <h4>{row.title}</h4>
                    <p>{row.body}</p>
                  </div>
                  <div className="dbx-insights-narrative-value">
                    <strong>{row.value}</strong>
                    <span>{row.valueLabel}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </article>

        <article className="dbx-panel">
          <div className="dbx-panel-head">
            <div>
              <h3>Signal Strength by Theme</h3>
              <p>Relative weight of key insight themes across remark categories.</p>
            </div>
            <span className="dbx-pill">Signal index</span>
          </div>
          <div className="dbx-insights-matrix" aria-label="Insight signal strength chart">
            {signalMatrix.rows.map((row) => (
              <div key={row.label} className="dbx-insights-matrix-row">
                <div className="dbx-insights-matrix-label">{row.label}</div>
                <div className="dbx-insights-matrix-track">
                  <div
                    className={`dbx-insights-matrix-fill ${row.barClass}`.trim()}
                    style={{ width: `${row.widthPct}%` }}
                  />
                </div>
                <div className="dbx-insights-matrix-value">{row.displayValue}</div>
              </div>
            ))}
          </div>
          <div className="dbx-insights-matrix-caption">
            <b>Interpretation:</b> {signalMatrix.interpretation}
          </div>
        </article>
      </section>

      <section className="dbx-action-plan">
        <InterventionActionPlan
          aiContent={aiMarkdown}
          defaultRoot={defaultRoot}
          defaultRecs={defaultRecs}
          defaultActions={defaultActions}
        />
      </section>

      <div className="dbx-section-title" id="dbx-insights-recommendations">
        <div>
          <h2>Recommended Actions</h2>
          <p>
            Recommendations are sequenced by effort and governance value so officers can move directly
            from insight to execution.
          </p>
        </div>
        <button type="button" className="dbx-section-action" onClick={() => scrollToId("dbx-insights-hero")}>
          Back to top ↑
        </button>
      </div>

      <section className="dbx-insights-two-column" id="dbx-insights-actions">
        <article className="dbx-panel dbx-table-card">
          <div className="dbx-table-head">
            <div>
              <h3>Prioritized Insight-to-Action Queue</h3>
              <p>Suggested district-level action plan for the next review cycle.</p>
            </div>
            <span className="dbx-pill">Officer ready</span>
          </div>
          <div className="dbx-insights-table-scroll">
            <table className="dbx-overview-table dbx-insights-table" aria-label="Insight action queue">
              <thead>
                <tr>
                  <th>Priority</th>
                  <th>Insight Theme</th>
                  <th>Evidence</th>
                  <th>Recommended Owner</th>
                  <th>Action</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {actionQueue.map((row) => (
                  <tr key={row.priority}>
                    <td>{row.priority}</td>
                    <td>
                      <strong>{row.theme}</strong>
                    </td>
                    <td>{row.evidence}</td>
                    <td>{row.owner}</td>
                    <td>{row.action}</td>
                    <td>
                      <span className={`dbx-tag ${row.tagClass}`.trim()}>{row.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </article>

        <article className="dbx-panel">
          <div className="dbx-panel-head">
            <div>
              <h3>Governance Decision Rules</h3>
              <p>Clear rules to convert insight signals into follow-up actions.</p>
            </div>
            <span className="dbx-pill">Playbook</span>
          </div>
          <div className="dbx-insights-playbook">
            {playbook.items.map((item) => {
              const Icon = PLAYBOOK_ICONS[item.iconClass] ?? AlertTriangle;
              return (
                <div key={item.title} className="dbx-insights-playbook-item">
                  <div className={`dbx-icon-box ${item.iconClass}`.trim()}>
                    <Icon size={22} />
                  </div>
                  <div>
                    <h4>{item.title}</h4>
                    <p>{item.body}</p>
                  </div>
                </div>
              );
            })}
            <div className="dbx-insights-playbook-footer">
              <b>{playbook.footerTitle}</b>
              <span>{playbook.footerBody}</span>
            </div>
          </div>
        </article>
      </section>
    </div>
  );
};

export default DropboxInsightsTab;
